import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ApiError, type InternalGroupSummary } from '../api/client.ts';
import { Can } from '../features/access/Can.tsx';
import { usePermissions } from '../features/access/usePermissions.ts';

const TYPES = [
  { key: 'isContentTest', label: 'Content test', hint: 'recipients of test sends' },
  { key: 'isSeed', label: 'Seed group', hint: 'gets a copy of every email send' },
  { key: 'isEventLog', label: 'Event log', hint: 'detailed SDK event log' },
] as const;
type TypeKey = (typeof TYPES)[number]['key'];

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** Settings → Internal Groups (BB-19): test users whose facts are marked `is_test`. */
export function InternalGroupsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/internal-groups' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/internal-groups' });
  const queryClient = useQueryClient();
  const queryKey = ['internal-groups', environmentId];
  const groups = useQuery({ queryKey, queryFn: () => api.listInternalGroups(environmentId) });
  const [filter, setFilter] = useState<'' | TypeKey>('');
  const [selected, setSelected] = useState<InternalGroupSummary | null>(null);
  const [name, setName] = useState('');
  const [types, setTypes] = useState<Record<TypeKey, boolean>>({ isContentTest: true, isSeed: false, isEventLog: false });
  const create = useMutation({
    mutationFn: () => api.createInternalGroup(environmentId, { name: name.trim(), ...types }),
    onSuccess: () => (setName(''), queryClient.invalidateQueries({ queryKey })),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) create.mutate();
  };
  const visible = (groups.data ?? []).filter((g) => !filter || g[filter]);

  return (
    <section>
      <h1>Internal Groups</h1>
      <p className="hint">Facts of members are marked as test data and left out of business reports.</p>
      <label>
        Type{' '}
        <select aria-label="Filter by type" value={filter} onChange={(e) => setFilter(e.target.value as '' | TypeKey)}>
          <option value="">All types</option>
          {TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      {groups.isPending ? (
        <p aria-busy="true">Loading groups…</p>
      ) : groups.isError ? (
        <p role="alert">Groups could not be loaded.</p>
      ) : visible.length === 0 ? (
        <p className="empty-state">No internal groups{filter ? ' of this type' : ''}.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Types</th>
              <th scope="col">Members</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((group) => (
              <tr key={group.id}>
                <td>{group.name}</td>
                <td>
                  {TYPES.filter((t) => group[t.key]).map((t) => (
                    <span key={t.key} className="tag tag--neutral">
                      {t.label}
                    </span>
                  ))}
                </td>
                <td>{group.memberCount}</td>
                <td>
                  <button type="button" onClick={() => setSelected(group)} aria-label={`Members of ${group.name}`}>
                    Members
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Can permission="messages.test_send">
        <form className="stack-form" aria-label="Create internal group" onSubmit={submit}>
          <label>
            Group name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="QA" />
          </label>
          <fieldset>
            <legend>Types</legend>
            {TYPES.map((t) => (
              <label key={t.key} title={t.hint}>
                <input type="checkbox" checked={types[t.key]} onChange={(e) => setTypes({ ...types, [t.key]: e.target.checked })} /> {t.label}
              </label>
            ))}
          </fieldset>
          <button type="submit" disabled={create.isPending || !name.trim() || !Object.values(types).some(Boolean)}>
            Create group
          </button>
          {create.isError && <span role="alert">{errorText(create.error)}</span>}
        </form>
      </Can>

      {selected && (
        <Can permission="profiles.view" fallback={<p className="hint">Member lists need the profiles.view permission.</p>}>
          <Members key={selected.id} environmentId={environmentId} group={selected} onChanged={() => queryClient.invalidateQueries({ queryKey })} />
        </Can>
      )}
    </section>
  );
}

function Members({ environmentId, group, onChanged }: { environmentId: string; group: InternalGroupSummary; onChanged: () => void }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/internal-groups' });
  const queryClient = useQueryClient();
  const piiView = usePermissions('environment').has('pii.view');
  const queryKey = ['group-members', environmentId, group.id];
  const members = useQuery({ queryKey, queryFn: () => api.listGroupMembers(environmentId, group.id) });
  const [value, setValue] = useState('');
  const refresh = () => (void queryClient.invalidateQueries({ queryKey }), onChanged());
  const add = useMutation({
    mutationFn: () => api.addGroupMember(environmentId, group.id, value.includes('@') ? { email: value.trim() } : { externalId: value.trim() }),
    onSuccess: () => (setValue(''), refresh()),
  });
  const remove = useMutation({ mutationFn: (profileId: string) => api.removeGroupMember(environmentId, group.id, profileId), onSuccess: refresh });

  return (
    <section aria-label={`Members of ${group.name}`} className="grants">
      <h2>Members of {group.name}</h2>
      {members.isPending ? (
        <p aria-busy="true">Loading members…</p>
      ) : (members.data ?? []).length === 0 ? (
        <p className="empty-state">No members yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">External ID</th>
              <th scope="col">Email</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.data!.map((m) => (
              <tr key={m.profileId}>
                <td>{m.externalId ?? `anonymous ${m.profileId.slice(0, 8)}`}</td>
                <td>{m.email ?? m.emailMasked ?? '—'}</td>
                <td>
                  <Can permission="messages.test_send">
                    <button type="button" onClick={() => remove.mutate(m.profileId)} aria-label={`Remove ${m.externalId ?? m.profileId}`}>
                      Remove
                    </button>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Can permission="messages.test_send">
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) add.mutate();
          }}
        >
          <input
            aria-label="External ID or email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={piiView ? 'External ID or email' : 'External ID'}
          />
          <button type="submit" disabled={add.isPending}>
            Add member
          </button>
          {add.isError && <span role="alert">{errorText(add.error)}</span>}
        </form>
      </Can>
    </section>
  );
}
